import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { EvaluationContext } from "./EvaluationContext";
import { CatalogContext } from "./CatalogContext";
import { UserContext } from "../../../pages/UserContext";
import useProduct from "../../../hooks/useProduct.hook";
import useReport from "../../../hooks/useReport.hook";
import {
  ACR_EDITOR_BASE_URL,
  ACR_EDITOR_OVERVIEW_URL,
  ENTITY_TYPE_AGENCY,
  ENTITY_TYPE_VENDOR,
  PRODUCT_UI_STATE_ADD_NEW,
  PRODUCT_UI_STATE_SEARCH,
  PRODUCT_VISIBILITY_PUBLIC,
} from "../../../utlities/core.constants";
import { getOverallCompletionPercentage } from "../utils/completionProgress";
import sanitizeReportPayloadForSave from "../utils/reportPayloadSanitization";
import {
  blankPayloadFields,
  getValidationErrorsFromResponse,
  mapReportApiValidationErrors,
} from "../utils/reportApiErrorMapping";

export const EditorNavigationContext = React.createContext(null);

/** Validation messages for product fields. Used when show_product_validation_errors is true and a field is empty. */
export const PRODUCT_FIELD_VALIDATION_MESSAGES = {
  name: "Please include a name for your new product.",
  version: "Please include a version for your new product.",
  description: "Please include a description for your new product.",
  category: "Please include a category for your new product.",
};

/** True if we should attempt report save (have report_id to PUT, or have user content to POST). Avoids POSTing an empty payload. */
export function evaluationHasContentToSave(evaluation) {
  if (!evaluation?.catalog) return false;
  if (evaluation.report_id != null) return true;
  const p = evaluation.product || {};
  const hasProduct =
    (p.name && String(p.name).trim()) ||
    (p.version && String(p.version).trim()) ||
    (p.description && String(p.description).trim());
  const hasReportMeta =
    (evaluation.report_date && String(evaluation.report_date).trim()) ||
    (evaluation.notes && String(evaluation.notes).trim());
  const chapters = evaluation.chapters || {};
  const hasChapterContent =
    typeof chapters === "object" &&
    Object.keys(chapters).length > 0 &&
    Object.keys(chapters).some((chapterId) => {
      const ch = chapters[chapterId];
      if (!ch?.criteria?.length) return false;
      return ch.criteria.some(
        (c) =>
          (c.components || []).some(
            (comp) =>
              (comp.adherence?.level && String(comp.adherence.level).trim()) ||
              (comp.adherence?.notes && String(comp.adherence.notes).trim())
          )
      );
    });
  return !!(hasProduct || hasReportMeta || hasChapterContent);
}

/**
 * True if an in-progress "add new product" has content the user would lose by navigating away.
 * Used to make the leave prompt dirty-based rather than mode-based, so we never warn about
 * "unsaved changes" when the add-new form is untouched (GSAACR-356).
 */
export function addNewProductHasUnsavedContent(evaluation) {
  const p = evaluation?.product || {};
  const productFields = [
    p.name,
    p.version,
    p.description,
    p.category,
    p.pscCode,
    p.fedRampStatus,
  ];
  const hasProductInput = productFields.some(
    (value) => value != null && String(value).trim() !== ""
  );
  const poc = p.productPoc || {};
  const hasPocInput = Object.values(poc).some(
    (value) => value != null && String(value).trim() !== ""
  );
  return hasProductInput || hasPocInput;
}

/** True if the path targets the editor's About page (where the add-new product form lives). */
export function isAboutPagePath(path) {
  return String(path || "").replace(/\/$/, "").endsWith("/about");
}

/**
 * Whether a navigation is leaving the in-progress "add new product" step. The add-new form lives on
 * the About page, so navigating *to* About is staying in the step (you're going there to continue
 * it); heading to any other step is leaving it. The in-progress product is preserved either way —
 * the caller guides the author to finish it rather than discarding it (GSAACR-345).
 */
export function isLeavingAddNewProductStep(evaluation, targetPath) {
  if (evaluation?.product_ui_state !== PRODUCT_UI_STATE_ADD_NEW) return false;
  return !isAboutPagePath(targetPath);
}

function requireEvaluation(evaluation, callerName) {
  if (evaluation == null || typeof evaluation !== "object") {
    throw new Error(`${callerName} requires a non-null evaluation object.`);
  }
  return evaluation;
}

/**
 * Resolve owner fields (vendor vs. agency) for a report payload from the authenticated user.
 * Reports are owned by exactly one org; the backend authoritatively re-derives ownership from the
 * user, but we send matching fields so names/display stay consistent for both org types.
 */
export function resolveReportOwnerFields(userEntity, vendor) {
  const orgId = userEntity?.entity?.id ?? null;
  const orgName = userEntity?.entity?.name ?? "";
  const isVendor = userEntity?.type === ENTITY_TYPE_VENDOR;
  const isAgency = userEntity?.type === ENTITY_TYPE_AGENCY;
  const vendorContactName = vendor?.company_name ?? vendor?.name ?? "";
  return {
    vendorId: isVendor ? orgId : null,
    agencyId: isAgency ? orgId : null,
    // Vendors default the report's vendor name to their own org; agencies only use what the buyer typed.
    vendorName: vendorContactName || (isVendor ? orgName : ""),
    agencyName: isAgency ? orgName : "",
  };
}

/** Build the report payload shape expected by the backend for POST */
function buildReportPayload(evaluation, userEntity) {
  const safeEvaluation = requireEvaluation(evaluation, "buildReportPayload");
  const product = safeEvaluation.product ?? {};
  const author = safeEvaluation.author ?? {};
  const vendor = safeEvaluation.vendor ?? {};
  const relatedAcrs = Array.isArray(safeEvaluation.related_acrs)
    ? safeEvaluation.related_acrs
      .map((item) => String(item?.url ?? "").trim())
      .filter(Boolean)
    : [];
  const ownerFields = resolveReportOwnerFields(userEntity, vendor);
  const payload = {
    catalogId: safeEvaluation.catalog ?? null,
    vendorId: ownerFields.vendorId,
    agencyId: ownerFields.agencyId,
    productId: product.id ?? null,
    productName: product.name ?? "",
    vendorName: ownerFields.vendorName,
    agencyName: ownerFields.agencyName,
    version: safeEvaluation.version ?? "",
    reportDetails: {
      version: safeEvaluation.version ?? "",
      notes: safeEvaluation.notes ?? "",
      evaluationMethods: safeEvaluation.evaluation_methods_used ?? "",
      legalDisclaimer: safeEvaluation.legal_disclaimer ?? "",
      repositoryUrl: safeEvaluation.repository ?? "",
      feedbackUrl: safeEvaluation.feedback ?? "",
      license: safeEvaluation.license ?? "",
      relatedAcrs,
    },
    author: {
      name: author.name ?? "",
      companyName: author.company_name ?? "",
      address: author.address ?? "",
      email: author.email ?? "",
      phone: author.phone ?? "",
      websiteUrl: author.website ?? "",
    },
    vendorContact: {
      name: vendor.name ?? "",
      email: vendor.email ?? "",
      phone: vendor.phone ?? "",
      websiteUrl: vendor.website ?? "",
    },
    isPublic: false,
  };
  // Never send a value the API is known to reject for format (email/phone/URL) -- leaves the
  // evaluation state (and any inline About-page error) untouched, only blanks the outgoing field.
  return sanitizeReportPayloadForSave(payload, safeEvaluation);
}

/**
 * Map evaluation chapters/criteria to ReportResponseDataModel[] for UpdateReportRequest.Responses.
 * One entry per (criterion, component) where the user has set level or notes.
 * Conformance level is the stored enum name (Supports, PartiallySupports, etc.).
 */
function buildReportResponses(evaluation) {
  const chapters = evaluation?.chapters ?? {};
  if (typeof chapters !== "object") return [];
  return Object.keys(chapters).flatMap((chapterId) => {
    const ch = chapters[chapterId];
    const criteriaList = ch?.criteria ?? [];
    return criteriaList.flatMap((criterion) => {
      const criterionId = criterion.num != null ? String(criterion.num) : null;
      if (criterionId == null) return [];
      const components = criterion.components ?? [];
      return components
        .map((comp) => {
          const level = comp.adherence?.level != null ? String(comp.adherence.level).trim() : "";
          const notes = comp.adherence?.notes != null ? String(comp.adherence.notes).trim() : "";
          if (level === "" && notes === "") return null;
          return {
            criterionId,
            component: comp.name ?? null,
            conformanceLevel: level || null,
            notes: notes || null,
          };
        })
        .filter(Boolean);
    });
  });
}

/** Build the update payload shape expected by the backend (UpdateReportRequest). */
function buildUpdateReportPayload(evaluation, userEntity) {
  const safeEvaluation = requireEvaluation(evaluation, "buildUpdateReportPayload");
  const product = safeEvaluation.product ?? {};
  const author = safeEvaluation.author ?? {};
  const vendor = safeEvaluation.vendor ?? {};
  const relatedAcrs = Array.isArray(safeEvaluation.related_acrs)
    ? safeEvaluation.related_acrs
      .map((item) => String(item?.url ?? "").trim())
      .filter(Boolean)
    : [];
  const ownerFields = resolveReportOwnerFields(userEntity, vendor);
  const payload = {
    catalogId: safeEvaluation.catalog ?? null,
    vendorId: ownerFields.vendorId,
    agencyId: ownerFields.agencyId,
    productId: product.id ?? null,
    version: safeEvaluation.version ?? "",
    isPublic: false,
    productName: product.name ?? "",
    vendorName: ownerFields.vendorName,
    agencyName: ownerFields.agencyName,
    reportDetails: {
      version: safeEvaluation.version ?? "",
      notes: safeEvaluation.notes ?? "",
      evaluationMethods: safeEvaluation.evaluation_methods_used ?? "",
      legalDisclaimer: safeEvaluation.legal_disclaimer ?? "",
      repositoryUrl: safeEvaluation.repository ?? "",
      feedbackUrl: safeEvaluation.feedback ?? "",
      license: safeEvaluation.license ?? "",
      relatedAcrs,
    },
    author: {
      name: author.name ?? "",
      companyName: author.company_name ?? "",
      address: author.address ?? "",
      email: author.email ?? "",
      phone: author.phone ?? "",
      websiteUrl: author.website ?? "",
    },
    vendorContact: {
      name: vendor.name ?? "",
      email: vendor.email ?? "",
      phone: vendor.phone ?? "",
      websiteUrl: vendor.website ?? "",
    },
    responses: buildReportResponses(safeEvaluation),
  };
  // Never send a value the API is known to reject for format (email/phone/URL) -- leaves the
  // evaluation state (and any inline About-page error) untouched, only blanks the outgoing field.
  return sanitizeReportPayloadForSave(payload, safeEvaluation);
}

function hasProductId(productId) {
  return productId != null && productId !== "";
}

/** Idle delay before a background autosave fires, so a typed sentence produces ~one save (GSAACR-356). */
const AUTOSAVE_DEBOUNCE_MS = 2500;
/** Poll interval used to defer a queued autosave (or a nav-triggered save) while another is in flight. */
const AUTOSAVE_BUSY_RETRY_MS = 800;
/** Cap on busy-retries for a nav-triggered save so a stuck save pipeline can't trap navigation. */
const AUTOSAVE_BUSY_MAX_RETRIES = 10;

const defaultSleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Run saveFn, retrying while it reports `busy` (another save in flight) so a nav-triggered save
 * isn't silently dropped when a background autosave is holding the save lock (GSAACR-356). Bounded
 * by maxAttempts so a persistently busy pipeline can't trap the caller. sleep is injectable for tests.
 */
export async function saveRetryingWhileBusy(
  saveFn,
  { maxAttempts = AUTOSAVE_BUSY_MAX_RETRIES, retryDelayMs = AUTOSAVE_BUSY_RETRY_MS, sleep = defaultSleep } = {}
) {
  const result = await saveFn();
  // Retries are inherently sequential (each depends on the previous result plus a delay), so this
  // recurses rather than looping with an await inside it. Bounded by maxAttempts.
  if (!result?.busy || maxAttempts <= 0) return result;
  await sleep(retryDelayMs);
  return saveRetryingWhileBusy(saveFn, { maxAttempts: maxAttempts - 1, retryDelayMs, sleep });
}

/**
 * Fingerprint of exactly what a save would send, so background autosave can skip no-op saves and
 * only fire when the savable content actually changed (not on derived-only churn like completion %).
 */
export function autosaveFingerprint(evaluation, userEntity) {
  const payload = evaluation?.report_id != null
    ? buildUpdateReportPayload(evaluation, userEntity)
    : buildReportPayload(evaluation, userEntity);
  return JSON.stringify(payload);
}

const REPORT_SAVE_ERROR_MESSAGES = {
  offline: "You appear to be working offline. Please check your connection to resume autosaving.",
  serverUnavailable: "Autosave service is unavailable right now. Please try again shortly. You can download your report to save progress.",
};

function resolveReportSaveErrorMessage() {
  const isOfflineByNavigator =
    typeof navigator !== "undefined" && navigator.onLine === false;
  if (isOfflineByNavigator) {
    return REPORT_SAVE_ERROR_MESSAGES.offline;
  }
  return REPORT_SAVE_ERROR_MESSAGES.serverUnavailable;
}

/** Runs before-navigate logic (cancels new product in add_mode if user agrees, save report as draft/update), then navigates. */
export function EditorNavigationProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { evaluation, setEvaluation } = useContext(EvaluationContext);
  const resetProductAddMode = useCallback((prev) => ({
    ...prev,
    report_id: null,
    report_save_status: "idle",
    report_save_error: undefined,
    product_ui_state: PRODUCT_UI_STATE_SEARCH,
    show_product_validation_errors: false,
    product_submit_error: undefined,
    product: {
      ...prev.product,
      id: undefined,
      name: "",
      version: "",
      description: "",
      category: "",
      pscCode: "",
      fedRampStatus: "",
      visibility: PRODUCT_VISIBILITY_PUBLIC,
      hiddenAgencyIds: [],
      isPublished: undefined,
    },
  }), []);
  const { getCatalog } = useContext(CatalogContext);
  const { userEntity } = useContext(UserContext);
  useProduct();
  const { createReport, updateReport } = useReport();
  const isSubmittingReportRef = useRef(false);
  // Bumped when the author tries to leave an in-progress new product from the About page. The About
  // page watches it to snap focus to the Product section and surface a finish-it reminder, instead
  // of an OS discard prompt (GSAACR-345).
  const [finishProductNudge, setFinishProductNudge] = useState(0);
  const signalFinishProduct = useCallback(() => setFinishProductNudge((n) => n + 1), []);
  // Latest evaluation for closures (autosave timeout, leave guard) that must read current state
  // without re-subscribing on every keystroke.
  const evaluationRef = useRef(evaluation);
  useEffect(() => {
    evaluationRef.current = evaluation;
  }, [evaluation]);

  const computeCompletionPercentage = useCallback(() => {
    const catalog = getCatalog(evaluation?.catalog);
    const chapters = catalog?.chapters ?? [];
    return getOverallCompletionPercentage(evaluation, chapters);
  }, [evaluation, getCatalog]);

  // Persist the current evaluation to the server (POST for a new report, PUT for an existing one)
  // WITHOUT navigating. Shared by the on-nav flush, the background autosave, and the leave guard so
  // there is a single save pipeline (GSAACR-356). Returns a structured result rather than throwing.
  const saveReport = useCallback(
    async () => {
      const payload = buildReportPayload(evaluation, userEntity);
      const shouldSkipSave =
        !evaluation?.catalog ||
        !evaluationHasContentToSave(evaluation) ||
        Boolean(evaluation?.product_is_unknown) ||
        !hasProductId(payload.productId);
      if (shouldSkipSave) return { ok: true, skipped: true };
      // Another save (nav flush / prior autosave) is still in flight; let the caller retry.
      if (isSubmittingReportRef.current) return { ok: false, busy: true };

      setEvaluation((prev) => ({
        ...prev,
        report_save_status: "saving",
        report_save_error: undefined,
      }));
      isSubmittingReportRef.current = true;
      try {
        const reportId = evaluation.report_id;
        const { percentage } = computeCompletionPercentage();
        let updatePayload = reportId
          ? { ...buildUpdateReportPayload(evaluation, userEntity), completionPercentage: percentage }
          : { ...payload, completionPercentage: percentage };
        const attemptSave = (body) => (reportId
          ? updateReport(reportId, body)
          : createReport(body));

        let response = await attemptSave(updatePayload);
        let strippedFieldErrors = null;

        // Defense in depth: sanitizeReportPayloadForSave should already prevent this, but if the
        // API rejects a field we don't validate identically client-side (e.g. a parity gap),
        // blank exactly the fields it flagged and retry once rather than failing the whole save.
        const firstAttemptErrors = getValidationErrorsFromResponse(response);
        if (firstAttemptErrors) {
          const { fieldErrors, matchedEvaluationPaths } = mapReportApiValidationErrors(firstAttemptErrors);
          if (matchedEvaluationPaths.length > 0) {
            updatePayload = blankPayloadFields(updatePayload, matchedEvaluationPaths);
            strippedFieldErrors = fieldErrors;
            response = await attemptSave(updatePayload);
          }
        }

        const ok = response && response.status >= 200 && response.status < 300;
        const newId = response?.data?.id;

        if (ok && (reportId || newId != null)) {
          setEvaluation((prev) => ({
            ...prev,
            ...(!reportId && newId != null ? { report_id: newId } : {}),
            about_field_format_errors: strippedFieldErrors
              ? { ...(prev.about_field_format_errors ?? {}), ...strippedFieldErrors }
              : prev.about_field_format_errors,
            report_save_status: strippedFieldErrors ? "partial" : "saved",
            report_save_error: strippedFieldErrors
              ? "Report saved, but some fields couldn't be saved due to formatting and were left blank. Review the About page to fix them."
              : undefined,
          }));
          return { ok: true };
        }

        // The API rejected the save with validation errors: mark failed so the caller stays put
        // rather than navigating away from work the server did not persist.
        const remainingValidationErrors = getValidationErrorsFromResponse(response);
        if (remainingValidationErrors) {
          setEvaluation((prev) => ({
            ...prev,
            about_field_format_errors: strippedFieldErrors
              ? { ...(prev.about_field_format_errors ?? {}), ...strippedFieldErrors }
              : prev.about_field_format_errors,
            report_save_status: "failed",
            report_save_error: "We couldn't save your report right now. Please review your entries and try again.",
          }));
          return { ok: false };
        }

        const message = resolveReportSaveErrorMessage();
        setEvaluation((prev) => ({
          ...prev,
          report_save_status: "failed",
          report_save_error: message,
        }));
        return { ok: false };
      } catch {
        const message = resolveReportSaveErrorMessage();
        setEvaluation((prev) => ({
          ...prev,
          report_save_status: "failed",
          report_save_error: message,
        }));
        return { ok: false };
      } finally {
        isSubmittingReportRef.current = false;
      }
    },
    [
      evaluation,
      userEntity,
      setEvaluation,
      createReport,
      updateReport,
      computeCompletionPercentage,
    ]
  );

  const saveReportThenNavigate = useCallback(
    async (targetPath) => {
      // Retry past an in-flight save (a background autosave can hold the save lock for seconds) so a
      // step-nav click isn't silently dropped. Navigate on success/skip, and also once retries are
      // exhausted while still busy (that save completes on the still-mounted SPA); stay put only on
      // an explicit failure so the user can see the error rather than leaving unsaved work.
      const result = await saveRetryingWhileBusy(saveReport);
      if (result.ok || result.skipped || result.busy) navigate(targetPath);
    },
    [saveReport, navigate]
  );

  // Background autosave: persist to the server a couple seconds after the user stops typing, so
  // work is safe even without a step-navigation (GSAACR-356 — previously a WCAG chapter edited
  // then abandoned via the app nav was never saved). No-op saves are skipped via a fingerprint of
  // exactly what would be sent, and a save is deferred while another is in flight.
  const autosaveTimerRef = useRef(null);
  const lastAutosavedFingerprintRef = useRef(null);
  useEffect(() => {
    const payload = buildReportPayload(evaluation, userEntity);
    const canAutosave =
      Boolean(evaluation?.catalog) &&
      evaluationHasContentToSave(evaluation) &&
      !evaluation?.product_is_unknown &&
      hasProductId(payload.productId);
    if (!canAutosave) return undefined;

    const fingerprint = autosaveFingerprint(evaluation, userEntity);
    if (fingerprint === lastAutosavedFingerprintRef.current) return undefined; // nothing new to save

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    const attempt = () => {
      // Defer past an in-flight save (nav flush or a prior autosave) instead of racing it.
      if (isSubmittingReportRef.current) {
        autosaveTimerRef.current = setTimeout(attempt, AUTOSAVE_BUSY_RETRY_MS);
        return;
      }
      saveReport().then((result) => {
        // Only remember the fingerprint on a real save; a failed/skipped save should retry on the
        // next edit rather than being suppressed as a no-op.
        if (result?.ok && !result.skipped) lastAutosavedFingerprintRef.current = fingerprint;
      });
    };
    autosaveTimerRef.current = setTimeout(attempt, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [evaluation, userEntity, saveReport]);

  // Leave guard for navigating *out of* the editor entirely (e.g. clicking the global "Product
  // Listings" nav), which bypasses the step-nav's save. On the way out we flush a best-effort save
  // (the SPA stays mounted, so the request completes), and we only interrupt with a prompt when the
  // last save actually failed — otherwise the exit is seamless (GSAACR-356).
  const leaveWithFailedSavePrompt =
    "Your most recent changes haven't been saved to the server yet. Leave the editor anyway?";
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        const leavingEditor =
          currentLocation.pathname.startsWith(ACR_EDITOR_BASE_URL) &&
          !nextLocation.pathname.startsWith(ACR_EDITOR_BASE_URL);
        return leavingEditor && evaluationHasContentToSave(evaluationRef.current);
      },
      []
    )
  );
  const isHandlingBlockerRef = useRef(false);
  useEffect(() => {
    if (blocker.state !== "blocked") {
      isHandlingBlockerRef.current = false;
      return;
    }
    if (isHandlingBlockerRef.current) return;
    isHandlingBlockerRef.current = true;
    // Flush on the way out regardless of the branch below.
    saveReport();
    if (evaluationRef.current?.report_save_status === "failed") {
      // eslint-disable-next-line no-alert -- deliberate leave-guard prompt for an unsaved-to-server report
      if (window.confirm(leaveWithFailedSavePrompt)) {
        blocker.proceed();
        return;
      }
      blocker.reset();
      isHandlingBlockerRef.current = false;
      return;
    }
    blocker.proceed();
  }, [blocker, saveReport, leaveWithFailedSavePrompt]);

  // Hard close / refresh / external navigation: the SPA is torn down, so a network save can't be
  // relied on. Warn only when a save is genuinely at risk (in flight or failed) — the on-device
  // copy (flushed by EvaluationContext) plus reopen recovery cover the rest.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const status = evaluationRef.current?.report_save_status;
      if (status !== "saving" && status !== "failed") return;
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign -- required by the browser beforeunload API
      event.returnValue = leaveWithFailedSavePrompt;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [leaveWithFailedSavePrompt]);

  const navigateTo = useCallback(
    async (targetPath) => {
      // Read the latest evaluation (not a stale closure) for the add-new checks below.
      const { current: latestEvaluation } = evaluationRef;
      const currentPath = location.pathname || "";
      const pathNormalized = currentPath.replace(/\/$/, "") || "/";

      if (isLeavingAddNewProductStep(latestEvaluation, targetPath)) {
        if (addNewProductHasUnsavedContent(latestEvaluation)) {
          // The in-progress product is real work, so never discard it. When the author is on the
          // About page (where the form lives) and tries to step away, keep them here and nudge the
          // Product section into view with a finish-it reminder. From any other page the product
          // isn't being edited, so let the move happen — the editor banner explains why autosave and
          // submit stay off until it's saved (GSAACR-345).
          if (isAboutPagePath(pathNormalized)) {
            signalFinishProduct();
            return;
          }
        } else {
          // An untouched add-new form has nothing to keep; quietly fall back to search so the state
          // doesn't linger (and no reminder/banner shows for empty input).
          setEvaluation((prev) => resetProductAddMode(prev));
        }
      }

      // The index (My Draft ACRs landing) and Overview help page have no report input, so don't attempt
      // a report save when navigating away from them.
      const isOnOverviewPage =
        pathNormalized === ACR_EDITOR_BASE_URL || pathNormalized === ACR_EDITOR_OVERVIEW_URL;
      if (isOnOverviewPage) {
        navigate(targetPath);
        return;
      }

      await saveReportThenNavigate(targetPath);
    },
    [
      location.pathname,
      saveReportThenNavigate,
      navigate,
      resetProductAddMode,
      setEvaluation,
      signalFinishProduct,
    ]
  );

  const value = useMemo(
    () => ({ navigateTo, finishProductNudge }),
    [navigateTo, finishProductNudge]
  );

  return (
    <EditorNavigationContext.Provider value={value}>
      {children}
    </EditorNavigationContext.Provider>
  );
}

EditorNavigationProvider.propTypes = {
  children: PropTypes.node,
};

EditorNavigationProvider.defaultProps = {
  children: null,
};

export function useEditorNavigation() {
  const ctx = useContext(EditorNavigationContext);
  return ctx;
}
